import React, { useState } from "react";
import {
	Box,
	Card,
	CardMedia,
	CardContent,
	Typography,
	Chip,
	Divider,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/BrokenImage";
import { Util } from "../../../util/Util";
import { API_BASE_URL } from "../../../util/constants";
import { useTranslation } from "react-i18next";

interface InventoryCardProps {
	item: any;
	setSelectedItem: (item: any) => void;
}

const InventoryCard = React.memo(({ item, setSelectedItem }: InventoryCardProps) => {
	const { t } = useTranslation("common");
	const [imgError, setImgError] = useState(false);
	const imgSrc = `${API_BASE_URL}uploads/items/${item.item_id}-main.jpg`;

	const stockBadge = item.quantity === 0
		? { label: t("inventory.outOfStock"), color: "error" as const }
		: item.quantity > 0 && item.quantity < 10
			? { label: t("inventory.lowStock"), color: "warning" as const }
			: null;

	return (
		<Card sx={{
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			transition: 'transform 0.2s, box-shadow 0.2s',
			'&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
			borderRadius: 3,
			cursor: 'pointer',
		}}
			onClick={() => setSelectedItem(item)}
		>
			<Box sx={{ position: 'relative', pt: '100%', bgcolor: 'action.hover' }}>
				{imgError ? (
					<Box sx={{
						position: 'absolute',
						top: 0,
						width: '100%',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						color: 'text.disabled',
					}}>
						<ImageIcon sx={{ fontSize: 48, mb: 1 }} />
						<Typography variant="caption" color="text.disabled">
							{item.item_code}
						</Typography>
					</Box>
				) : (
					<CardMedia
						component="img"
						image={imgSrc}
						onError={() => setImgError(true)}
						alt={item.item_name || t('common:tables.itemImage')}
						loading="lazy"
						sx={{
							position: 'absolute',
							top: 0,
							width: '100%',
							height: '100%',
							objectFit: 'contain',
							p: 2,
						}}
					/>
				)}
				{stockBadge && (
					<Chip
						label={stockBadge.label}
						color={stockBadge.color}
						size="small"
						sx={{ position: 'absolute', top: 12, left: 12, fontWeight: 600 }}
					/>
				)}
				<Chip
					label={item.category}
					size="small"
					sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 600 }}
				/>
			</Box>

			<CardContent sx={{ flexGrow: 1, pt: 2 }}>
				<Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
					{item.item_code}
				</Typography>
				<Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, lineHeight: 1.2, height: '2.4em', overflow: 'hidden' }}>
					{item.item_name}
				</Typography>

				<Divider sx={{ my: 1.5 }} />

				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Box>
						<Typography variant="caption" color="text.secondary" display="block">
							{t("inventory.stock").toUpperCase()}
						</Typography>
						<Typography variant="body1" fontWeight="700">
							{Util.formatNumber(item.quantity)}
						</Typography>
					</Box>
					<Box sx={{ textAlign: 'right' }}>
						<Typography variant="caption" color="text.secondary" display="block">
							{t("inventory.price").toUpperCase()}
						</Typography>
						<Typography variant="body1" fontWeight="700" color="primary.main">
							{Util.formatNumber(item.total)}
						</Typography>
					</Box>
				</Box>
			</CardContent>

			<Box sx={{ p: 2, pt: 0 }}>
				<Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
					{t("ui.labels.value")}: {Util.formatNumber(item.Value)}
				</Typography>
			</Box>
		</Card>
	);
}, (prev, next) => prev.item.item_id === next.item.item_id);

export default InventoryCard;
